import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, generatePath } from "react-router-dom";
import { useTranslation } from "i18n";
import config from "config/global";
import {
  BreadcrumbBar,
  IBreadcrumbBarItem,
  PageHeader,
  Spinner,
  CommonErrorPopup,
  Head,
} from "component-library";
import {
  AddOnEntitlements,
  PremiumEntitlements,
  MemberGroups,
} from "shared-types";
import { doesUserHaveEntitlements, getUserGroup } from "shared-auth";

import "./AgentSearchPage.scss";

import {
  IPerformer,
  PerformerType,
  SearchOrder,
} from "shared/models/Performers";
import PerformerService from "shared/services/api/performers/PerformerService";
import { ActionType } from "store/types";
import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";
import { IAgentQuickSearchPageTranslation } from "models/translation";
import { ControllableSearch } from "shared/components/ControllableSearch/ControllableSearch";
import { PerformerResults } from "components/PerformerResults/PerformerResults";
import { pushToDataLayer } from "app-shell/GoogleTagManager/GoogleTagManager";
import {
  TRACKING_EVENT_TYPE,
  TRACKING_VALUES,
} from "app-shell/GoogleTagManager/enums";
import { AGENT_SEARCH_FULL_PATH } from "config/common";

interface IAgentSearchPageProps {
  breadcrumbs: IBreadcrumbBarItem[];
  translations: IAgentQuickSearchPageTranslation;
  pageTitle: string;
}

export const PREMIUM_TAKE_COUNT = 100;

export const AgentSearchPage = ({
  pageTitle,
  breadcrumbs,
  translations,
}: IAgentSearchPageProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { searchTerm } = useParams<{ searchTerm?: string }>();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(searchTerm || "");
  const [performers, setPerformers] = useState<IPerformer[]>();
  const [requestError, setRequestError] = useState<AxiosError | null>(null);
  const [loadingPerformers, setLoadingPerformers] = useState(false);
  const [premiumPage, setPremiumPage] = useState(1);

  const goToMyhomePage = () => {
    window.location.replace(`${config.siteRootUrl}myhome`);
  };

  useEffect(() => {
    dispatch({ type: ActionType.SetLoading, payload: false });
  }, []);

  const handleAgentSearch = async (searchTerm: string) => {
    setLoadingPerformers(true);

    const performerListResponse = await PerformerService.searchPerformers({
      skip: 0,
      take: PREMIUM_TAKE_COUNT,
      performerName: searchTerm,
      orderBy: SearchOrder.Relevance,
      performerTypes: [PerformerType.Graduate, PerformerType.Performer],
      ExecutedSearch: true,
    });

    if (performerListResponse && performerListResponse instanceof AxiosError) {
      setRequestError(performerListResponse);

      return;
    }

    setPerformers(performerListResponse.profiles);
    setLoadingPerformers(false);
    setPremiumPage(1);

    const isPremierLEGACY = doesUserHaveEntitlements(
      PremiumEntitlements.Premium_Site_Access
    );
    const hasAddOnBundle = doesUserHaveEntitlements(
      AddOnEntitlements.AddOnBundle
    );
    const isEntitled = isPremierLEGACY || hasAddOnBundle;
    const userGroup = getUserGroup();

    const isPerformer = userGroup === MemberGroups.performer;

    if (isEntitled && isPerformer) {
      pushToDataLayer({
        event: TRACKING_EVENT_TYPE.PerformerSearch,
        searchTerm,
        memberType: userGroup,
        memberTier: TRACKING_VALUES.PremierMember,
      });
    }
  };

  useEffect(() => {
    if (searchTerm) {
      handleAgentSearch(searchTerm);
    } else {
      setPerformers(undefined);
      setRequestError(null);
      setSearchValue("");
    }
  }, [searchTerm]);

  const updateSearchTerm = (term?: string) =>
    navigate(generatePath(AGENT_SEARCH_FULL_PATH, { searchTerm: term || "" }));

  if (requestError) {
    return (
      <CommonErrorPopup
        errorStatusCode={requestError.response?.status}
        popupProps={{
          close: goToMyhomePage,
        }}
        alertMessageProps={{
          buttons: [
            {
              name: t("common:button.label.close"),
              type: "primary",
              click: goToMyhomePage,
            },
          ],
        }}
      />
    );
  }

  return (
    <div className="agent-search-page">
      <Head
        registerPageView={() =>
          GTMEvents.jobsFeed.pageViews.agentQuickSearch(pageTitle)
        }
      >
        <title>{pageTitle}</title>
      </Head>

      <div className="agent-search-page__header">
        <BreadcrumbBar items={breadcrumbs} />
        <PageHeader
          pageTitle={translations.header.title}
          bottomSection={
            <div className="g-inner-grid-12">
              <div className="g-col-lg-6">
                <ControllableSearch
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={translations.body.agentSearchPlaceholder}
                  onSearch={updateSearchTerm}
                  onClear={() => updateSearchTerm("")}
                />
              </div>
            </div>
          }
        />
      </div>

      <main className="agent-search-page__main">
        {loadingPerformers && <Spinner />}

        {!loadingPerformers &&
          (!performers || (performers && performers.length === 0)) && (
            <div className="agent-search-page__no-results">
              <h1 className="agent-search-page__no-results--title">
                {translations.body.noResults}
              </h1>
              <p className="agent-search-page__no-results--desc">
                {translations.body.noResultsFoundDescription}
              </p>
            </div>
          )}

        {!loadingPerformers && performers && performers.length > 0 && (
          <PerformerResults
            performers={performers}
            page={premiumPage}
            changePage={setPremiumPage}
          />
        )}
      </main>
    </div>
  );
};
